import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBadgeCheck,
  faCommentAltTimes,
} from '@fortawesome/pro-solid-svg-icons'
import { useSelector } from 'react-redux'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import Button from 'design-system/button'
import { EvaluationResult } from 'models/evaluationResult.model'
import { IMainState } from 'store/states'
import { useIsDesktop } from 'helpers'
import { EvaluationStatesEnum } from 'enum/evaluation-states.enum'
import Evaluation, { IUserAnswer } from '../../Evaluation'
import styles from './EvaluationModal.module.scss'

interface IEvaluationModal {
  data: EvaluationResult
  answers: IUserAnswer[]
  show: boolean
  onClose: any
  canEdit: boolean
  setOnEvaluation: any
}
interface IResult {
  approved: boolean
  correctCount: number
  points: {
    acquired: number
    total: number
  }
  attempts: {
    used: number
    total: number
  }
  showResults: boolean
}
const EvaluationModal: React.FC<IEvaluationModal> = ({
  data,
  answers,
  show,
  onClose,
  canEdit,
  setOnEvaluation,
}) => {
  const { t } = useTranslation()
  const isDesktop = useIsDesktop()
  const [showModalResults, setShowModalResults] = useState(false)
  const { lesson } = useSelector(({ lmsLessons }: IMainState) => lmsLessons)
  const config = useSelector(
    ({ blocks }: IMainState) => blocks.evaluation.config
  )
  if (!config) return null
  const result: IResult = {
    approved: data.approved,
    correctCount: data.correct,
    attempts: { used: data.attemps, total: config.maxRetries },
    points: {
      acquired: data.points,
      total: config.approvalPoints,
    },
    showResults: config.showResults,
  }

  const handleCheck = () => {
    if (isDesktop) {
      window.open(`/evaluation/${data.blockEvaluationId}/check`)
      onClose()
      window.scrollTo(0, 0)
    } else {
      setShowModalResults(true)
    }
  }

  const handleOnClose = () => {
    setShowModalResults(false)
  }

  return (
    <>
      <Modal
        show={show}
        size="lg"
        centered
        className={`${styles.modalContainer}`}
        onHide={onClose}
      >
        <Modal.Header
          translate="yes"
          closeButton
          className={`${styles.modalHeader} d-flex align-items-center`}
        >
          <h1 className="title m-0">
            {t(
              `lessons.contentBlocks.evaluationBlock.resultsModal.title.${
                result.approved ? 'approved' : 'failed'
              }`
            )}
          </h1>
        </Modal.Header>

        <Modal.Body className="px-4 py-4">
          <div className="w-100 d-flex justify-content-center mb-3">
            <FontAwesomeIcon
              icon={result.approved ? faBadgeCheck : faCommentAltTimes}
              size="6x"
              color={result.approved ? '#36b37e' : '#ec5b56'}
            />
          </div>
          <div className={styles.resultTitle}>
            <h2>
              {t(
                `lessons.contentBlocks.evaluationBlock.resultsModal.${
                  result.approved ? 'congratulations' : 'pity'
                }`
              )}
            </h2>
          </div>
          <div className={`${styles.text} ${styles.result}`}>
            <span>
              {t(
                `lessons.contentBlocks.evaluationBlock.resultsModal.${
                  result.approved ? 'approvedWith' : 'failedWith'
                }`
              )}{' '}
              <b>
                {t(
                  `lessons.contentBlocks.evaluationBlock.resultsModal.pointsAndCorrectAnswers`,
                  {
                    points: result.points ? result.points.acquired : 0,
                    correct: result.correctCount + '/' + answers.length,
                  }
                )}{' '}
              </b>
            </span>
          </div>
          <div className={styles.text}>
            <span>
              <b>
                {t(
                  `lessons.contentBlocks.evaluationBlock.resultsModal.requiredScore`
                )}
                :
              </b>{' '}
              {result.points && result.points.total}{' '}
              {t('lessons.contentBlocks.evaluationBlock.resultsModal.points')}
            </span>
          </div>
          <div className={styles.text}>
            <span>
              <b>
                {t(
                  `lessons.contentBlocks.evaluationBlock.resultsModal.attemptsNumber`
                )}
                :
              </b>{' '}
              {` ${t(
                'lessons.contentBlocks.evaluationBlock.resultsModal.attempts',
                {
                  current: result.attempts && result.attempts.used,
                  total: result.attempts && result.attempts.total,
                }
              )}`}
            </span>
          </div>
        </Modal.Body>
        {
          <>
            <div className="px-4 d-flex justify-content-center">
              <div className="dropdown-divider m-0 w-100 " />
            </div>
            <Modal.Footer className={`border-0 ${styles.buttons}`}>
              {result.showResults && (
                <Button
                  size={ButtonSizes.Medium}
                  outline
                  variant={ButtonVariant.Primary}
                  className="my-0"
                  onClick={handleCheck}
                >
                  {t(
                    `lessons.contentBlocks.evaluationBlock.resultsModal.buttons.correct`
                  )}
                </Button>
              )}
              <Button
                size={ButtonSizes.Medium}
                variant={ButtonVariant.Primary}
                className="my-0"
                onClick={onClose}
              >
                {t(
                  `lessons.contentBlocks.evaluationBlock.resultsModal.buttons.continue`
                )}
              </Button>
            </Modal.Footer>
          </>
        }
      </Modal>
      {showModalResults && (
        <Modal
          show={showModalResults}
          onClose={handleOnClose}
          dialogClassName={'modal-fullscreen'}
        >
          {lesson && lesson.blockInstances && lesson.courseId && (
            <Modal.Body>
              <Evaluation
                state={EvaluationStatesEnum.onCheck}
                courseId={lesson.courseId}
                blockInstances={lesson.blockInstances}
                lessonId={lesson.id}
                evaluationId={data.blockEvaluationId}
                canEdit={canEdit}
                setOnEvaluation={() => setOnEvaluation(false)}
                onClose={handleOnClose}
              />
            </Modal.Body>
          )}
        </Modal>
      )}
    </>
  )
}

export default EvaluationModal
